import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react'
import { Alert } from '@vnetwork/datacentral-ui/feedback'
import { Button } from '@vnetwork/datacentral-ui/primitives'
import { AxiosError } from 'axios'

/** Extract HTTP status code from error */
const getStatusCode = (error: unknown): number => {
    if (error && typeof error === 'object' && 'response' in error) {
        return (error as AxiosError).response?.status ?? 500
    }
    if (error && typeof error === 'object' && 'status' in error) {
        return (error as { status: number }).status
    }
    return 500
}

/** Friendly user message based on error type */
const getUserMessage = (error: unknown, t: TFunction): string => {
    const status = getStatusCode(error)
    if (status === 401 || status === 403) return t('query-error.forbidden')
    if (status === 404) return t('query-error.not-found')
    if (status >= 500) return t('query-error.server')
    if (error instanceof Error && error.message?.includes('Network Error'))
        return t('query-error.network')
    return t('query-error.generic')
}

interface QueryErrorAlertProps {
    error: unknown
    onRetry?: () => void
}

/** Inline error alert with friendly message and collapsible technical details */
const QueryErrorAlert = ({ error, onRetry }: QueryErrorAlertProps) => {
    const { t } = useTranslation()
    const [showDetail, setShowDetail] = useState(false)
    const message = getUserMessage(error, t)
    const detail = error instanceof Error ? error.message : String(error)

    return (
        <Alert
            tone="danger"
            title={message}
            actions={
                <>
                    {onRetry && (
                        <Button
                            size="sm"
                            variant="ghost"
                            startIcon={<RotateCcw size={14} />}
                            onClick={onRetry}
                        >
                            {t('query-error.retry')}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="ghost"
                        endIcon={
                            showDetail ? (
                                <ChevronUp size={14} />
                            ) : (
                                <ChevronDown size={14} />
                            )
                        }
                        onClick={() => setShowDetail((v) => !v)}
                    >
                        {t('detail')}
                    </Button>
                </>
            }
        >
            {showDetail && (
                <pre className="text-xs bg-surface/60 rounded-sm p-2 overflow-x-auto whitespace-pre-wrap break-all">
                    {detail}
                </pre>
            )}
        </Alert>
    )
}

export default QueryErrorAlert
